/**
 * CDB20DeclarationChecklist - TASK-376
 * Seven mandatory CDB 20 declarations as checkboxes.
 * All must be checked for Form A completion.
 */

import React from 'react';
import { ShieldCheck, AlertTriangle, CheckCircle2, Circle } from 'lucide-react';

export interface CDB20Declarations {
  identity_confirmed: boolean;
  beneficial_owner_confirmed: boolean;
  source_of_funds_confirmed: boolean;
  tax_compliance_confirmed: boolean;
  pep_declaration_confirmed: boolean;
  information_accuracy_confirmed: boolean;
  reporting_obligation_confirmed: boolean;
}

interface CDB20DeclarationChecklistProps {
  declarations: CDB20Declarations;
  onChange: (key: keyof CDB20Declarations, value: boolean) => void;
  readOnly?: boolean;
  showValidation?: boolean;
}

interface DeclarationConfig {
  key: keyof CDB20Declarations;
  label: string;
  description: string;
}

const DECLARATIONS: DeclarationConfig[] = [
  {
    key: 'identity_confirmed',
    label: '1. Identity Confirmation',
    description:
      'I confirm that the identity of the account holder has been verified in accordance with FINMA regulations and the Swiss Anti-Money Laundering Act.',
  },
  {
    key: 'beneficial_owner_confirmed',
    label: '2. Beneficial Owner Declaration',
    description:
      'I declare the beneficial owner(s) of the assets deposited in this business relationship as specified in this form.',
  },
  {
    key: 'source_of_funds_confirmed',
    label: '3. Source of Funds',
    description:
      'I confirm that the source of funds deposited into this account has been identified and documented in compliance with CDB 20 requirements.',
  },
  {
    key: 'tax_compliance_confirmed',
    label: '4. Tax Compliance',
    description:
      'I confirm that the account holder has been informed of their tax obligations and that the information provided is consistent with tax compliance requirements.',
  },
  {
    key: 'pep_declaration_confirmed',
    label: '5. PEP Declaration',
    description:
      'I have determined whether the account holder or beneficial owner is a Politically Exposed Person (PEP) and have applied enhanced due diligence where required.',
  },
  {
    key: 'information_accuracy_confirmed',
    label: '6. Information Accuracy',
    description:
      'I confirm that all information provided in this declaration is true, complete, and accurate to the best of my knowledge.',
  },
  {
    key: 'reporting_obligation_confirmed',
    label: '7. Reporting Obligation',
    description:
      'I acknowledge the obligation to report any suspicious activity and to update this declaration if any material changes occur.',
  },
];

export const CDB20DeclarationChecklist: React.FC<CDB20DeclarationChecklistProps> = ({
  declarations,
  onChange,
  readOnly = false,
  showValidation = false,
}) => {
  const allChecked = DECLARATIONS.every((d) => declarations[d.key]);
  const checkedCount = DECLARATIONS.filter((d) => declarations[d.key]).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-gray-500" />
          <h4 className="text-sm font-semibold text-gray-900">
            CDB 20 Declarations
          </h4>
        </div>
        <span
          className={`text-xs font-medium px-2 py-1 rounded ${
            allChecked
              ? 'bg-green-50 text-green-700'
              : 'bg-gray-100 text-gray-600'
          }`}
        >
          {checkedCount}/{DECLARATIONS.length} confirmed
        </span>
      </div>

      {showValidation && !allChecked && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
          <p className="text-xs text-amber-700">
            All {DECLARATIONS.length} declarations must be confirmed before Form A
            can be marked as completed.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {DECLARATIONS.map((decl) => {
          const isChecked = declarations[decl.key];

          return (
            <label
              key={decl.key}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                isChecked
                  ? 'bg-green-50 border-green-200'
                  : 'bg-white border-gray-200 hover:bg-gray-50'
              } ${readOnly ? 'cursor-default' : ''}`}
            >
              <div className="pt-0.5 shrink-0">
                {isChecked ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <Circle className="w-5 h-5 text-gray-300" />
                )}
              </div>
              <input
                type="checkbox"
                checked={isChecked}
                onChange={(e) => onChange(decl.key, e.target.checked)}
                disabled={readOnly}
                className="sr-only"
              />
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-medium ${
                    isChecked ? 'text-green-900' : 'text-gray-900'
                  }`}
                >
                  {decl.label}
                </p>
                <p
                  className={`text-xs mt-0.5 leading-relaxed ${
                    isChecked ? 'text-green-700' : 'text-gray-500'
                  }`}
                >
                  {decl.description}
                </p>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default CDB20DeclarationChecklist;
